import { useState, useEffect, useCallback } from "react";
import { Tooltip, CircularProgress, Box } from "@mui/material";
import axios from "axios";

/**
 * @typedef {Object} LazyTooltipProps
 * @property {string} endpoint - The API endpoint to fetch tooltip data from.
 * @property {number} [count=0] - The count value to determine tooltip activation.
 * @property {React.ReactNode|function} children - The content to display inside the tooltip trigger.
 */

/**
 * LazyTooltip component with lazy-loaded tooltip content.
 *
 * @param {LazyTooltipProps} props
 * @returns {JSX.Element}
 */
export default function LazyTooltip({ endpoint, count = 0, children }) {
  const [tooltipData, setTooltipData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [hasHovered, setHasHovered] = useState(false);

  const fetchTooltipData = useCallback(async () => {
    if (!endpoint) return;
    
    setLoading(true);
    try {
      const response = await axios.get(endpoint, {
        headers: { "Content-Type": "application/json" },
        withCredentials: true,
      });
      
      setTooltipData(response.data);
    } catch (err) {
      setError("Failed to load data");
    } finally {
      setLoading(false);
    }
  }, [endpoint]);

  useEffect(() => {
    if (hasHovered && !tooltipData && !loading && !error && count > 0) {
      fetchTooltipData();
    }
  }, [hasHovered, count, tooltipData, loading, error, fetchTooltipData]);

  const handleMouseEnter = () => {
    if (!hasHovered && count > 0) {
      setHasHovered(true);
    }
  };

  const getTooltipContent = () => {
    // Don't show tooltip if count is 0
    if (count === 0) {
      return "";
    }

    if (loading) {
      return (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
          <CircularProgress size={12} sx={{ color: "#ffffff" }} />
          <span>Loading...</span>
        </Box>
      );
    }
    
    if (error) {
      return error;
    }
    
    if (tooltipData && tooltipData.names) {
      return tooltipData.names;
    }
    
    return "Hover to load names...";
  };

  return (
    <Tooltip
      title={getTooltipContent()}
      placement="top"
      arrow
      onOpen={handleMouseEnter}
      disableHoverListener={count === 0}
      slotProps={{
        tooltip: {
          sx: {
            backgroundColor: '#000000',
            color: '#ffffff',
            fontSize: '0.875rem',
            padding: '8px 12px',
            maxWidth: '400px',
            zIndex: 999999,
            '& .MuiTooltip-arrow': {
              color: '#000000',
            }
          }
        },
        popper: {
          sx: {
            zIndex: 999999
          }
        }
      }}
    >
      <span style={{ display: 'inline-block', cursor: 'default' }}>
        {typeof children === 'function' ? children(count) : children}
      </span>
    </Tooltip>
  );
}

